import { Injectable } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { BASE_API_URL, IAmenity, IListing, IPhoto, ISite, ISiteWhere } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import {
  CachedHttpClient,
  Logger,
  OfflineSiteDbSet,
  OfflineSitePhotoEntity,
  OfflineSitePhotosDbSet,
} from '@curbnturf/network';
import { PhotoService } from '@curbnturf/photo';
import { StatusFacade } from '@curbnturf/status';
import { ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, takeWhile } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

const API_URL = BASE_API_URL + 'listing';

@Injectable({
  providedIn: 'root',
})
export class OfflineSiteService {
  // For this service, start with the assumption that we are online.
  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'cellular' };

  get isOnline() {
    return this.connectionStatus.connected;
  }

  constructor(
    private http: CachedHttpClient,
    private logger: Logger,
    private offlineSiteDbSet: OfflineSiteDbSet,
    private offlineSitePhotosDbSet: OfflineSitePhotosDbSet,
    private photoService: PhotoService,
    private statusFacade: StatusFacade,
    private store: Store,
    private toastController: ToastController,
  ) {
    this.statusFacade.networkStatus$.subscribe((status) => {
      this.connectionStatus = status;
    });
  }

  create(site: ISite): Observable<ISite> {
    const storableSite = { ...site };
    if (!site.syncId) {
      storableSite.syncId = uuidv4();
    }
    // @todo check for photos to update and store those for later upload
    return from(this.offlineSiteDbSet.store(storableSite)).pipe(map(() => storableSite));
  }

  createAmenity(site: ISite): Observable<ISite> {
    const amenities: IAmenity[] = [];
    if (site.amenities) {
      amenities.push(...site.amenities);
    }

    amenities.push({ site });

    return this.update({ ...site, amenities });
  }

  fetch(syncId: string): Observable<ISite> {
    return from(this.offlineSiteDbSet.retrieve(syncId));
  }

  fetchAll(query: ISiteWhere): Observable<ISite[]> {
    const queryString = objectToUrlParams(query);
    return this.http.get<IListing[]>(`${API_URL}?${queryString}`);
  }

  remove(syncId: string): Observable<ISite> {
    return from(this.offlineSiteDbSet.retrieve(syncId)).pipe(
      mergeMap((siteToRemove: IListing) => {
        // remove photos
        if (siteToRemove.photos && siteToRemove.photos.length > 0) {
          return forkJoin(
            siteToRemove.photos?.map((photo) => {
              return from(this.offlineSitePhotosDbSet.removeByKey(photo.key)).pipe(
                map(() => {
                  return from(this.photoService.removeOfflinePhoto(photo.key)).pipe(
                    map(() => {
                      return siteToRemove;
                    }),
                  );
                }),
              );
            }),
          ).pipe(mergeMap(() => from(this.offlineSiteDbSet.remove(syncId)).pipe(map(() => siteToRemove))));
        } else {
          return from(this.offlineSiteDbSet.remove(syncId)).pipe(map(() => siteToRemove));
        }
      }),
    );
  }

  restore(siteId: number): Observable<ISite> {
    return this.http.post<ISite>(`${API_URL}/${siteId}/restore`, null);
  }

  removeAmenity(site: ISite, amenity: IAmenity): Observable<ISite> {
    if (!site.amenities) {
      return of(site);
    }

    const amenities = [...site.amenities];

    const index = amenities.indexOf(amenity);

    if (index || index === 0) {
      amenities.splice(index, 1);
    }

    return this.update({ ...site, amenities });
  }

  update(site: ISite): Observable<IListing> {
    // @todo check for photos to update and store those for later upload
    return from(this.offlineSiteDbSet.store(site)).pipe(map(() => site));
  }

  enable(site: ISite): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${site.id}/enable`, null);
  }

  disable(site: ISite): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${site.id}/disable`, null);
  }

  // @todo rework for offline functionality
  hide(site: ISite): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${site.id}/hide`, null);
  }

  // @todo rework for offline functionality
  show(site: ISite): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${site.id}/show`, null);
  }

  // @todo rework for offline functionality
  removeImage(siteId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${siteId}/remove-image/${photoIndex}`);
  }

  uploadImage(syncId: string, photoFormData: FormData): Observable<{ syncId: string; photos: IPhoto[] }> {
    const file = photoFormData.get('files') as File;
    const caption = photoFormData.get('captions') as string;
    const entity: OfflineSitePhotoEntity = {
      syncId: syncId,
      key: 'unknown',
      caption,
    };

    return from(this.photoService.saveOfflinePhoto(file)).pipe(
      takeWhile((filename) => filename !== false),
      mergeMap((filename) => {
        if (filename) {
          entity.key = filename;
          const photo: IPhoto = {
            key: filename,
            caption: entity.caption,
          };
          // Check that we haven't already saved this photo to the database.
          return from(this.offlineSitePhotosDbSet.retrieveByKey(entity.key)).pipe(
            mergeMap((result) => {
              if (result.length === 0) {
                return from(this.offlineSitePhotosDbSet.store(entity)).pipe(map(() => ({ photos: [photo], syncId })));
              }
              return of({ photos: [photo], syncId });
            }),
          );
        } else {
          throw new Error('Failed to save uploaded image.');
        }
      }),
    );
  }

  // @todo rework for offline functionality
  favorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/favorite`, null);
  }

  // @todo rework for offline functionality
  unfavorite(listing: IListing): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/unfavorite`, null);
  }

  uploadOfflinePhoto(photo: IPhoto, siteId: number, syncId: string): Observable<IPhoto | void> {
    this.logger.debug('Uploading Photo', { siteId, syncId, photo });
    return from(this.photoService.retrieveOfflinePhoto(photo.key)).pipe(
      mergeMap((file) => {
        if (file) {
          this.logger.debug('Photo found, proceeding to upload.', { siteId, syncId, photo });
          // Setup the file data to send to the server
          const formData = new FormData();
          formData.append('files', file);
          formData.append('captions', photo.caption || '');
          formData.append('listingId', siteId.toString());
          formData.append('syncId', syncId);

          return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${siteId}/upload-image`, formData).pipe(
            map((photoResult) => {
              this.logger.debug('Successfully uploaded image: ' + photo.key, {
                siteId,
                result: JSON.stringify(photoResult),
              });
              return photoResult.photos.pop();
            }),
            catchError((error) => {
              this.logger.error('Failed to upload listing image.', { siteId, photo, error });
              return of(error);
            }),
          );
        } else {
          this.logger.warning('Local photo not found.', { siteId, syncId, photo });
        }
        return of();
      }),
    );
  }
}
